import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useHistory } from 'react-router-dom';

import reducer from './reducer';

const defaultState = {
    isRetrieving: true,
    isCreating: false,
    applicationInstances: [],
    availableApplications: [],
};

/**
 * @typedef {Object} ApplicantDashboardController A set of states and functions that represent the entirety of the ApplicantDashboard's functionality
 * @property {boolean} isRetrieving A boolean value denoting whether the retrieve endpoint is still running
 * @property {boolean} isCreating A boolean value denoting whether the create endpoint is still running
 * @property {Array} applicationInstances An array of application instance objects to display
 * @property {Array} availableApplications An array of available application definitions for creation and messaging
 * @property {Function} retrieveApplications An asynchronous function for retrieving and handling the applications and defintions
 * @property {Function} createApplication An asynchronous function for handling the creation of a new application
 */

/**A Hook for managing the necessary functions and states that control the UI
 * @returns {ApplicantDashboardController} ApplicantDashboard states and functions
 */
const useApplications = () => {
    const [state, dispatch] = React.useReducer(reducer, defaultState);
    const history = useHistory();

    const retrieveApplications = async () => {
        dispatch({ type: 'retrieving' });
        try {
            let response = await API.get('ShipyardAPI', '/applications');

            dispatch({ type: 'update', payload: response });
        } catch (e) {
            toast.error(t('General.AjaxError'));
            console.error('An error occurred while retrieving application instances and definitions.');
        } finally {
            dispatch({ type: 'retrieved' });
        }
    };

    const createApplication = async () => {
        let chosenApplicationId = state?.availableApplications?.[0]?.id;
        let body = {
            applicationId: chosenApplicationId,
        };

        let id = toast.loading('Creating application...');
        dispatch({ type: 'creating' });
        try {
            let startNewResponse = await API.post('ShipyardAPI', '/application', {
                body,
            });

            if (!startNewResponse.applicationInstanceId) {
                toast.error(t('Applications.Create.Error'));
                console.error('An error occurred while creating a new application instance.');
                return;
            }

            toast.success('Application created.');
            history.push(`/application/${startNewResponse.applicationInstanceId}`);
        } catch (e) {
            toast.error(t('General.AjaxError'));
            dispatch({ type: 'createError' });
        } finally {
            toast.dismiss(id);
        }
    };

    return { ...state, retrieveApplications, createApplication };
};

export default useApplications;
